import type { EtsyDataSource, EtsyListing, ImportedProduct, ImportedShop } from "./contracts.js";
import { parseEtsyShopHandle } from "./shop-input.js";

export class ShopNotFoundError extends Error {
  readonly code = "shop_not_found";
}

function uniqueListings(featured: EtsyListing[], active: EtsyListing[]): EtsyListing[] {
  const byId = new Map<number, EtsyListing>();
  for (const listing of [...featured, ...active]) if (!byId.has(listing.listing_id)) byId.set(listing.listing_id, listing);
  return [...byId.values()].slice(0, 100);
}

function formatPrice(listing: EtsyListing): string {
  const amount = listing.price.amount / Math.max(1, listing.price.divisor);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: listing.price.currency_code }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${listing.price.currency_code}`;
  }
}

function normalizeProduct(listing: EtsyListing, featuredIds: Set<number>): ImportedProduct | null {
  const images = [...(listing.images ?? [])].sort((a, b) => a.rank - b.rank);
  const primary = images[0];
  if (!primary?.url_fullxfull && !primary?.url_570xN) return null;
  return {
    id: String(listing.listing_id),
    title: listing.title.trim(),
    description: listing.description?.trim(),
    price: formatPrice(listing),
    listingUrl: listing.url,
    primaryImage: primary.url_fullxfull || primary.url_570xN,
    alternateImages: images.slice(1).map((image) => image.url_fullxfull || image.url_570xN).filter(Boolean),
    imageWidth: primary.full_width,
    imageHeight: primary.full_height,
    favorites: listing.num_favorers,
    featured: featuredIds.has(listing.listing_id),
  };
}

export async function importEtsyShop(input: string, dataSource: EtsyDataSource): Promise<ImportedShop> {
  const handle = parseEtsyShopHandle(input);
  const matches = await dataSource.findShops(handle);
  const match = matches.find((shop) => shop.shop_name.toLowerCase() === handle.toLowerCase()) ?? matches[0];
  if (!match) throw new ShopNotFoundError(`No Etsy shop named “${handle}” was found in the ${dataSource.kind} data source.`);

  const shop = await dataSource.getShop(match.shop_id);
  if (!shop) throw new ShopNotFoundError(`The Etsy shop “${handle}” could not be loaded.`);

  const [featured, active] = await Promise.all([
    dataSource.getFeaturedListings(shop.shop_id, 12),
    dataSource.getActiveListings(shop.shop_id, 100),
  ]);
  const candidates = uniqueListings(featured, active);
  const detailed = await dataSource.getListingsByIds(candidates.map((item) => item.listing_id));
  const candidateOrder = new Map(candidates.map((item, index) => [item.listing_id, index]));
  const featuredIds = new Set(featured.map((item) => item.listing_id));
  const products = detailed
    .sort((a, b) => (candidateOrder.get(a.listing_id) ?? 999) - (candidateOrder.get(b.listing_id) ?? 999))
    .map((listing) => normalizeProduct(listing, featuredIds))
    .filter((product): product is ImportedProduct => product !== null);

  return {
    source: dataSource.kind,
    input,
    handle: shop.shop_name,
    id: String(shop.shop_id),
    name: shop.shop_name,
    title: shop.title?.trim() || undefined,
    announcement: shop.announcement?.trim() || undefined,
    shopUrl: shop.url,
    icon: shop.icon_url_fullxfull || undefined,
    banner: shop.image_url_760x100 || undefined,
    activeListingCount: shop.listing_active_count,
    reviewCount: shop.review_count,
    reviewAverage: shop.review_average,
    products,
  };
}
